// PO-GST-01 · P2-D2 · display-only helpers for the Management GST comparison (no GST calculation).
export const FIELD_LABELS = {
  expected_date: "Delivery date", remarks: "Remarks", place_of_supply_state_code: "Place of supply",
  company_is_receiving_location: "Received at company location", reverse_charge: "Reverse charge", charges: "Charges",
  qty_adjustment: "Adjust Qty", adjustment_notes: "Adjustment notes", unit_cost: "Rate", hsn_code: "HSN",
  tax_pct: "GST rate", gst_rate_source: "GST rate source", discount_pct: "Discount %", discount_amt: "Discount ₹",
};
export const fieldLabel = (k) => FIELD_LABELS[k] || k;

export const TOTAL_ROWS = [
  ["goods_taxable_before_charges", "Goods Value (excl. GST)"], ["discount_total", "Line discounts"],
  ["included_charges_total", "Charges included in goods"], ["service_charges_taxable", "Separate service charges"],
  ["taxable_total", "Taxable value"], ["cgst_total", "CGST"], ["sgst_total", "SGST"], ["igst_total", "IGST"],
  ["non_taxable_charges_total", "Non-taxable charges"], ["round_off", "Round off"],
  ["grand_total", "Grand Total (incl. GST)"],
];

// Display-only difference of two stored values; null when either side is unavailable.
export function totalDiff(cur, sub) {
  if (cur == null || sub == null) return null;
  return Math.round((Number(sub) - Number(cur)) * 100) / 100;
}

// "0% (confirmed)" only when the stored rate is 0 AND carries a source stamp; null = unresolved.
export function rateLabel(pct, source) {
  if (pct == null || pct === "") return "— unresolved";
  if (Number(pct) === 0) return source ? "0% (confirmed)" : "0% (unconfirmed)";
  return `${Number(pct)}%${source ? ` · ${source}` : ""}`;
}

export const shortFp = (v) => (v ? `${String(v).slice(0, 10)}…` : "—");
export const fmtTs = (v) => ((v || "").slice(0, 19).replace("T", " ") || "—");
export const money = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

export const chargeText = (c) => [c.label || c.code, money(c.amount), c.treatment,
  c.sac_code ? `SAC ${c.sac_code}` : "", c.gst_rate != null && c.treatment === "SEPARATE_SERVICE" ? `${c.gst_rate}%` : "",
  c.non_taxable_classification || ""].filter(Boolean).join(" · ");

// Approve-confirmation summary from stored queue values; null when data is missing/inconsistent.
export function approveSummary(row) {
  const a = row?.amendment;
  const g = a?.gst_comparison;
  if (!g) return null;
  const cur = g.current; const sub = g.proposed;
  const curRev = Number(a.revision ?? NaN);
  if (!cur?.totals || !sub?.totals || Number.isNaN(curRev) || sub.proposed_revision !== curRev + 1
      || cur.po_revision !== curRev) return null;
  return `${row.doc_no} · ${a.amendment_no}\nRev ${curRev} → Proposed Rev ${sub.proposed_revision}\n`
    + `Current Grand Total (incl. GST): ${money(cur.totals.grand_total)}\n`
    + `Submitted Grand Total (incl. GST): ${money(sub.totals.grand_total)}`;
}

export function approveConfirmText(row, fallback) {
  if (!row?.amendment?.gst_comparison) return fallback;
  const s = approveSummary(row);
  return s ? `Authorise GST amendment?\n\n${s}\n\nThe server re-validates GST before activation.`
    : `Authorise GST amendment ${row.doc_no} · ${row.amendment.amendment_no}?\n\nGST summary unavailable or inconsistent in the queue data — no summary is shown. The server re-validates GST before activation.`;
}

export const STALE_CODE = "PO_AMEND_GST_STALE";
export const STALE_MESSAGE = "Approval did not complete: the submitted GST calculation is out of date (Tax Master, supplier or PO data changed after submission). Nothing was changed. Use Reject to return the amendment to the buyer for correction and resubmission.";
export const isStaleGstError = (e) => {
  const d = e?.response?.data?.detail;
  return e?.response?.status === 409 && (d?.code === STALE_CODE || (typeof d === "string" && d.includes(STALE_CODE)));
};

// P2-D2-FIX01 · integrity of the STORED comparison for GST-controlled amendments. null = not applicable (LEGACY).
export function gstIntegrity(row) {
  const a = row?.amendment;
  const g = a?.gst_comparison;
  if (!a || !g) return null;
  const issues = [];
  const cur = g.current || null; const sub = g.proposed || null;
  const rev = a.revision;
  const isNum = (v) => v !== null && v !== undefined && v !== "" && !Number.isNaN(Number(v));
  if (!cur) issues.push("Current effective GST snapshot is missing");
  if (!sub) issues.push("Submitted GST calculation is missing");
  if (cur && (!isNum(cur.po_revision) || !isNum(cur.seq))) issues.push("Current revision / snapshot identity is missing");
  if (cur && !isNum(cur.totals?.grand_total)) issues.push("Current GST-inclusive grand total is unavailable");
  if (sub && !isNum(sub.proposed_revision)) issues.push("Submitted proposed revision is missing");
  if (sub && !sub.fingerprint) issues.push("Submitted calculation identity (fingerprint) is missing");
  if (sub && sub.tax_status !== "COMPUTED") issues.push(`Submitted tax status is ${sub.tax_status || "missing"}, not COMPUTED`);
  if (sub && !isNum(sub.totals?.grand_total)) issues.push("Submitted GST-inclusive grand total is unavailable");
  if (!isNum(rev)) issues.push("Amendment base revision is missing");
  if (isNum(rev) && isNum(a.base_revision) && Number(a.base_revision) !== Number(rev)) issues.push(`Amendment base revision ${a.base_revision} ≠ effective revision ${rev}`);
  if (isNum(rev) && cur && isNum(cur.po_revision) && Number(cur.po_revision) !== Number(rev)) issues.push(`Current snapshot revision ${cur.po_revision} ≠ effective revision ${rev}`);
  if (isNum(rev) && sub && isNum(sub.proposed_revision) && Number(sub.proposed_revision) !== Number(rev) + 1) issues.push(`Submitted proposed revision ${sub.proposed_revision} ≠ ${Number(rev) + 1}`);
  if (isNum(a.target_revision) && sub && isNum(sub.proposed_revision) && Number(a.target_revision) !== Number(sub.proposed_revision)) issues.push(`Target revision ${a.target_revision} ≠ submitted proposed revision ${sub.proposed_revision}`);
  return { ok: issues.length === 0, issues };
}

export const approveBlocked = (row) => { const r = gstIntegrity(row); return !!r && !r.ok; };
export const INTEGRITY_MESSAGE = "Approval unavailable: the stored GST comparison for this amendment is incomplete or inconsistent. No summary has been substituted and no fresh calculation was used. Reject remains available to return the amendment for correction and resubmission.";
